import { useState, type ReactElement } from 'react';
import { Avatar, Button, Tooltip } from '@fluentui/react-components';
import { AppsRegular, DismissRegular, LaptopRegular, PeopleRegular, PersonRegular } from '@fluentui/react-icons';
import { useApi } from '../../api/useApi';
import type { GroupMemberDto, GroupMemberKind } from '../../api/types';
import './groups.css';

const KIND_ICON: Record<GroupMemberKind, ReactElement> = {
  user: <PersonRegular />,
  group: <PeopleRegular />,
  device: <LaptopRegular />,
  servicePrincipal: <AppsRegular />,
  other: <AppsRegular />,
};

/** Typ-Zählung mit Pictogrammen (User / Gruppen / Geräte / Apps) + Rekursiv-Summe. */
export function MemberTypeBar({ members, transitiveCount }: { members: GroupMemberDto[]; transitiveCount?: number }) {
  const counts = { user: 0, group: 0, device: 0, servicePrincipal: 0 };
  for (const m of members) {
    if (m.kind === 'user') counts.user++;
    else if (m.kind === 'group') counts.group++;
    else if (m.kind === 'device') counts.device++;
    else if (m.kind === 'servicePrincipal') counts.servicePrincipal++;
  }
  const chips = [
    { icon: <PersonRegular />, label: 'User', n: counts.user },
    { icon: <PeopleRegular />, label: 'Gruppen', n: counts.group },
    { icon: <LaptopRegular />, label: 'Geräte', n: counts.device },
    { icon: <AppsRegular />, label: 'Apps', n: counts.servicePrincipal },
  ];
  return (
    <div className="grp-typebar">
      {chips.map((c) => (
        <span key={c.label} className={c.n === 0 ? 'grp-typechip zero' : 'grp-typechip'} title={c.label}>
          {c.icon}
          {c.n}
        </span>
      ))}
      {typeof transitiveCount === 'number' ? (
        <Tooltip relationship="description" content="Mitglieder inkl. verschachtelter Gruppen (rekursiv, dedupliziert)">
          <span className="grp-typechip total">rekursiv {transitiveCount}</span>
        </Tooltip>
      ) : null}
    </div>
  );
}

/** Mitglieder-/Owner-Zeile. Verschachtelte Gruppen: Count in Klammern + Hover-Mitgliederliste. */
export function MemberRow({
  member,
  onRemove,
  removeDisabled,
}: {
  member: GroupMemberDto;
  onRemove?: () => void;
  removeDisabled?: boolean;
}) {
  const api = useApi();
  const isUser = member.kind === 'user';
  const isGroup = member.kind === 'group';
  const [nested, setNested] = useState<string[] | null>(null);

  async function loadNested(visible: boolean) {
    if (!visible || nested !== null) return;
    try {
      const d = await api.getGroup(member.id);
      setNested(d.members.map((m) => m.displayName));
    } catch {
      setNested([]);
    }
  }

  const row = (
    <div className="grp-member">
      <Avatar
        name={member.displayName}
        size={28}
        color={isUser ? 'colorful' : 'neutral'}
        icon={isUser ? undefined : KIND_ICON[member.kind]}
      />
      <span className="grp-member-main">
        <span className={isUser ? 'grp-member-name pii' : 'grp-member-name'}>
          {member.displayName}
          {isGroup && member.nestedCount != null ? (
            <span className="grp-nested-count"> ({member.nestedCount})</span>
          ) : null}
        </span>
        <span className={isUser ? 'grp-member-sub pii' : 'grp-member-sub'}>{member.subtitle}</span>
      </span>
      {onRemove ? (
        <Button appearance="subtle" size="small" icon={<DismissRegular />} disabled={removeDisabled} onClick={onRemove}>
          Entfernen
        </Button>
      ) : null}
    </div>
  );

  if (!isGroup) return row;
  const tip =
    nested === null ? (
      'Mitglieder laden…'
    ) : nested.length === 0 ? (
      'Keine Mitglieder'
    ) : (
      <div className="pii">
        {nested.map((n, i) => (
          <div key={i}>{n}</div>
        ))}
      </div>
    );
  return (
    <Tooltip relationship="description" content={tip} onVisibleChange={(_, d) => void loadNested(d.visible)}>
      {row}
    </Tooltip>
  );
}
